import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyProgressService } from '../../lib/college-journey-service';
import { useAutoSave, SaveStatusIndicator, useDataRecovery } from '../../hooks/useAutoSave.jsx';
import DataRecoveryModal from '../Shared/DataRecoveryModal';

const StudentOnboardingFlow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [profileData, setProfileData] = useState({});
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [collegePreferences, setCollegePreferences] = useState({
    intendedMajors: [],
    geographicPreferences: '',
    sizePreference: '',
    settingPreference: '',
    keyCriteria: [],
    studentUniqueInsight: ''
  });
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { colleges, addCollege, academicProfile } = useCollegeContext();

  const totalSteps = 4;

  // Auto-save functionality - only include data that should trigger saves
  // Don't include currentStep as it changes frequently and causes excessive saves
  const onboardingData = useMemo(() => ({
    profileData,
    selectedColleges,
    collegePreferences,
    userId: currentUser?.id
  }), [profileData, selectedColleges, collegePreferences, currentUser?.id]);

  // Helper function to get the correct user ID for college_journey_progress table
  const getProgressUserId = async (familyMemberId) => {
    try {
      // college_journey_progress table expects auth.users(id), not family_members(id)
      // So we need to get the auth_user_id from family_members table
      const { data: familyData, error } = await supabase
        .from('family_members')
        .select('auth_user_id')
        .eq('id', familyMemberId)
        .single();
      
      if (error) {
        console.warn('Could not find auth_user_id, using family member id as fallback:', error);
        return familyMemberId;
      }
      
      return familyData?.auth_user_id || familyMemberId;
    } catch (error) {
      console.warn('Error mapping user ID, using fallback:', error);
      return familyMemberId;
    }
  };

  // Database save function
  const saveOnboardingProgress = async (data) => {
    if (!currentUser?.id) throw new Error('No user ID available');

    console.log('💾 Auto-saving onboarding progress:', data);

    // Get the correct user ID for the progress table (auth.users.id)
    const progressUserId = await getProgressUserId(currentUser.id);
    console.log('💾 Using progress user ID:', progressUserId, 'for family member:', currentUser.id);

    // Check existing status to avoid resetting completed onboarding
    const { data: existingProgress } = await supabase
      .from('college_journey_progress')
      .select('onboarding_complete')
      .eq('user_id', progressUserId)
      .single();

    // Preserve existing completion status if already true, otherwise calculate based on current step
    const onboardingComplete = existingProgress?.onboarding_complete === true
      ? true
      : currentStep >= totalSteps;

    // Save to our custom progress table
    const { error } = await supabase
      .from('college_journey_progress')
      .upsert({
        user_id: progressUserId,
        current_step: currentStep, // Use currentStep from component state, not data
        total_steps: totalSteps,
        profile_data: data.profileData,
        selected_colleges: data.selectedColleges,
        college_preferences: data.collegePreferences,
        onboarding_complete: onboardingComplete,
        last_updated: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) throw error;
    return data;
  };

  // Set up auto-save with longer delay to prevent excessive saves
  const autoSave = useAutoSave(onboardingData, saveOnboardingProgress, {
    delay: 3000, // Save 3 seconds after changes stop
    key: 'student-onboarding-progress',
    enabled: !!currentUser?.id,
    onSaveSuccess: () => console.log('✅ Onboarding progress saved'),
    onSaveError: (error) => console.error('❌ Failed to save onboarding progress:', error)
  });

  // Data recovery
  const recovery = useDataRecovery('student-onboarding-progress');

  useEffect(() => {
    const loadSavedProgress = async () => {
      if (!currentUser?.id) {
        loadInitialData();
        return;
      }

      // Check if user wants to start fresh (from localStorage flag)
      const startFresh = localStorage.getItem('student-onboarding-start-fresh') === 'true';
      if (startFresh) {
        console.log('🆕 Starting fresh - loading initial data');
        localStorage.removeItem('student-onboarding-start-fresh');
        loadInitialData();
        return;
      }

      // Check for saved progress in database first
      try {
        const progressUserId = await getProgressUserId(currentUser.id);
        console.log('🔍 Loading saved progress for user ID:', progressUserId);

        const { data: dbProgress, error: dbError } = await supabase
          .from('college_journey_progress')
          .select('*')
          .eq('user_id', progressUserId)
          .single();

        if (dbProgress && !dbError) {
          console.log('✅ Restored onboarding progress from database:', dbProgress);
          setCurrentStep(dbProgress.current_step || dbProgress.step || 1);
          
          // Only load saved data if it's not empty
          if (dbProgress.profile_data && Object.keys(dbProgress.profile_data).length > 0) {
            setProfileData(dbProgress.profile_data);
          } else {
            // Load initial data for profile but keep step
            loadInitialData();
          }
          
          setSelectedColleges(dbProgress.selected_colleges || []);
          setCollegePreferences(dbProgress.college_preferences || {});
          return;
        } else {
          console.log('🔍 No database progress found:', dbError?.message);
        }
      } catch (dbError) {
        console.warn('Failed to load database progress, trying localStorage:', dbError);
      }

      // Fallback to localStorage
      const savedProgress = localStorage.getItem('student-onboarding-progress');
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          setCurrentStep(progress.currentStep || 1);
          setProfileData(progress.profileData || {});
          setSelectedColleges(progress.selectedColleges || []);
          console.log('✅ Restored onboarding progress from localStorage:', progress);
          return;
        } catch (error) {
          console.error('Error parsing saved progress:', error);
        }
      }

      // If no saved progress, load fresh data
      loadInitialData();
    };

    loadSavedProgress();
  }, [currentUser, colleges]);

  const loadInitialData = async () => {
    if (!currentUser) return;

    try {
      console.log('🔍 Loading onboarding data for:', currentUser.name, currentUser.id);
      
      // Use the correct user ID for querying student_academic_profiles
      const profileUserId = await getProgressUserId(currentUser.id);
      console.log('🔍 Resolved profile user ID:', profileUserId);
      
      // Load academic profile data using the mapped user ID
      let dbProfileData = null;
      
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', profileUserId)
          .single();
        
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error loading profile:', profileError);
        } else {
          dbProfileData = profileData;
          console.log('📊 Loaded profile data:', dbProfileData);
        }
        
      } catch (dbError) {
        console.error('Database query error:', dbError);
        // Fall back to context data
        dbProfileData = academicProfile;
      }
      
      console.log('📊 Final database profile data:', dbProfileData);
      
      // Initialize comprehensive profile data from database or context
      const profile = {
        // Basic Info
        name: currentUser?.displayName || currentUser?.name || 'Student',
        graduationYear: dbProfileData?.graduation_year || academicProfile?.graduation_year || null,
        state: dbProfileData?.state_of_residency || academicProfile?.state_of_residency || null,
        highSchool: dbProfileData?.high_school_name || academicProfile?.high_school_name || null,
        highSchoolState: dbProfileData?.high_school_state || academicProfile?.high_school_state || null,
        highSchoolType: dbProfileData?.high_school_type || academicProfile?.high_school_type || null,
        
        // Academic Performance
        gpaWeighted: dbProfileData?.gpa_weighted || academicProfile?.gpa_weighted || null,
        gpaUnweighted: dbProfileData?.gpa_unweighted || academicProfile?.gpa_unweighted || null,
        gpaScale: dbProfileData?.gpa_scale || academicProfile?.gpa_scale || null,
        classRank: dbProfileData?.class_rank || academicProfile?.class_rank || null,
        classSize: dbProfileData?.class_size || academicProfile?.class_size || null,
        curriculumRigor: dbProfileData?.curriculum_rigor || academicProfile?.curriculum_rigor || null,
        
        // Test Scores
        satTotal: dbProfileData?.sat_total || academicProfile?.sat_total || null,
        satMath: dbProfileData?.sat_math || academicProfile?.sat_math || null,
        satReadingWriting: dbProfileData?.sat_reading_writing || academicProfile?.sat_reading_writing || null,
        satDate: dbProfileData?.sat_date || academicProfile?.sat_date || null,
        actComposite: dbProfileData?.act_composite || academicProfile?.act_composite || null,
        psatTotal: dbProfileData?.psat_total || academicProfile?.psat_total || null,
        nationalMeritStatus: dbProfileData?.national_merit_status || academicProfile?.national_merit_status || null,
        
        // Advanced Coursework
        apCoursesTaken: dbProfileData?.ap_courses_taken || academicProfile?.ap_courses_taken || null,
        apCoursesPlanned: dbProfileData?.ap_courses_planned || academicProfile?.ap_courses_planned || null,
        apExamsPassed: dbProfileData?.ap_exams_passed || academicProfile?.ap_exams_passed || null,
        ibDiplomaCandidate: dbProfileData?.ib_diploma_candidate || academicProfile?.ib_diploma_candidate || null,
        ibPredictedScore: dbProfileData?.ib_predicted_score || academicProfile?.ib_predicted_score || null,
        dualEnrollmentCredits: dbProfileData?.dual_enrollment_credits || academicProfile?.dual_enrollment_credits || null,
        honorsCourses: dbProfileData?.honors_courses || academicProfile?.honors_courses || null,
        
        // Academic Interests & Goals
        academicInterests: dbProfileData?.academic_interests || academicProfile?.academic_interests || null,
        intendedMajor: dbProfileData?.intended_major || academicProfile?.intended_major || null,
        intendedMinor: dbProfileData?.intended_minor || academicProfile?.intended_minor || null,
        careerGoals: dbProfileData?.career_goals || academicProfile?.career_goals || null,
        
        // Background & Special Circumstances
        firstGenerationCollege: dbProfileData?.first_generation_college || academicProfile?.first_generation_college || null,
        legacyConnections: dbProfileData?.legacy_connections || academicProfile?.legacy_connections || null,
        underrepresentedBackground: dbProfileData?.underrepresented_background || academicProfile?.underrepresented_background || null,
        specialCircumstances: dbProfileData?.special_circumstances || academicProfile?.special_circumstances || null
      };
      setProfileData(profile);
      console.log('Set profile data:', profile);

      // Load actual colleges from database
      if (colleges && colleges.length > 0) {
        // Transform existing colleges for onboarding display
        const collegeSelections = colleges.map(college => ({
          id: college.id || college.name?.toLowerCase().replace(/\s+/g, '_'),
          name: college.name || college.college_name,
          reason: college.parent_notes || college.reason || college.description || 'Great fit for your goals',
          parentAdded: college.added_by_parent || true,
          category: college.category || 'target'
        }));
        setSelectedColleges(collegeSelections);
        console.log('Loaded', collegeSelections.length, 'colleges from database:', collegeSelections);
      } else {
        // Fallback to sample colleges if none in database
        const defaultColleges = [
          { id: 'duke', name: 'Duke University', reason: 'Strong programs, great research opportunities', parentAdded: true },
          { id: 'mit', name: 'MIT', reason: 'Top-tier engineering and innovation focus', parentAdded: true },
          { id: 'uchicago', name: 'University of Chicago', reason: 'Rigorous academics, intellectual culture', parentAdded: false }
        ];
        setSelectedColleges(defaultColleges);
        console.log('Using fallback colleges');
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      // Use fallback data if loading fails
      setProfileData({
        name: currentUser?.displayName || 'Student',
        graduationYear: '2026',
        gpa: '3.85',
        intendedMajor: 'Computer Science',
        satScore: null
      });
    }
  };

  const nextStep = async () => {
    if (currentStep < totalSteps) {
      await saveCurrentStep();
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveCurrentStep = async () => {
    if (!currentUser?.id) return;
    
    try {
      // Save progress to database
      const progressData = {
        onboardingComplete: false,
        currentStep,
        totalSteps,
        profileData,
        selectedColleges,
        collegePreferences
      };
      
      await collegeJourneyProgressService.saveProgress(currentUser.id, progressData);
      console.log('✅ Saved onboarding progress to database');
      
      // Keep localStorage as backup for now
      const backupData = {
        currentStep,
        profileData,
        selectedColleges,
        collegePreferences,
        timestamp: Date.now()
      };
      localStorage.setItem('student-onboarding-progress', JSON.stringify(backupData));
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
      // Fallback to localStorage only
      const progressData = {
        currentStep,
        profileData,
        selectedColleges,
        collegePreferences,
        timestamp: Date.now()
      };
      localStorage.setItem('student-onboarding-progress', JSON.stringify(progressData));
    }
  };

  const saveAndExit = async () => {
    await saveCurrentStep();
    // In a real implementation, this would show a confirmation and possibly send an email
    alert('Progress saved! You can return anytime by clicking the link in your dashboard.');
    navigate('/dashboard');
  };

  const startAssessment = async () => {
    if (!currentUser?.id) return;
    
    try {
      // Save all final data and mark onboarding as complete
      const finalProgressData = {
        onboardingComplete: true,
        currentStep: totalSteps,
        totalSteps,
        profileData,
        selectedColleges,
        collegePreferences
      };
      
      await collegeJourneyProgressService.saveProgress(currentUser.id, finalProgressData);
      await collegeJourneyProgressService.markOnboardingComplete(currentUser.id);
      
      console.log('✅ Marked onboarding as complete in database');
      
      // Add selected colleges to user's list
      for (const college of selectedColleges) {
        await addCollege(college);
      }
      
      // Keep localStorage as backup for now
      const onboardingData = {
        profileData,
        collegePreferences,
        selectedColleges,
        completedAt: new Date().toISOString()
      };
      localStorage.setItem('student-onboarding-data', JSON.stringify(onboardingData));
      localStorage.setItem('college-onboarding-complete', 'true');
      localStorage.setItem('assessment-ready', 'true');
      
      // Navigate to the new conversational assessment
      navigate('/college/assessment');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Fallback to localStorage only
      const onboardingData = {
        profileData,
        collegePreferences,
        selectedColleges,
        completedAt: new Date().toISOString()
      };
      localStorage.setItem('student-onboarding-data', JSON.stringify(onboardingData));
      localStorage.setItem('college-onboarding-complete', 'true');
      localStorage.setItem('assessment-ready', 'true');
      
      // Navigate to the new conversational assessment
      navigate('/college/assessment');
    }
  };

  // Progress dots render function
  const renderProgressDots = () => {
    return Array.from({ length: totalSteps }, (_, i) => {
      const step = i + 1;
      let dotStyle = {
        borderRadius: '50%',
        transition: 'all 0.15s ease'
      };
      
      if (step < currentStep) {
        dotStyle = { ...dotStyle, width: '8px', height: '8px', background: '#0d9488' }; // completed
      } else if (step === currentStep) {
        dotStyle = { ...dotStyle, width: '12px', height: '12px', background: '#0f766e' }; // current (larger)
      } else {
        dotStyle = { ...dotStyle, width: '8px', height: '8px', background: '#d6d3d1' }; // upcoming
      }
      
      return <div key={step} style={dotStyle}></div>;
    });
  };

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#fafaf9',
      minHeight: '100vh',
      lineHeight: 1.5,
      color: '#0f172a'
    }}>
      {/* Progress Header */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        borderBottom: '2px solid #e7e5e4',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: '#0d9488',
              color: 'white',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 700
            }}>
              {currentStep}
            </div>
            <div>
              <div style={{ fontSize: '14px', color: '#57534e', fontWeight: 500 }}>
                Step {currentStep} of {totalSteps}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                {renderProgressDots()}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <SaveStatusIndicator 
              saveStatus={autoSave.saveStatus}
              lastSaved={autoSave.lastSaved}
              error={autoSave.error}
              className="text-sm"
            />
            <button
              onClick={saveAndExit}
              style={{
                fontSize: '14px',
                color: '#57534e',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'color 0.15s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#0f172a'}
              onMouseOut={(e) => e.target.style.color = '#57534e'}
            >
              Save & return later
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: 'calc(100vh - 80px)'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '2px solid #e7e5e4',
          maxWidth: '800px',
          width: '100%',
          padding: '40px'
        }}>
          {currentStep === 1 && (
            <ProfileReviewStep 
              profileData={profileData}
              setProfileData={setProfileData}
              onNext={nextStep}
            />
          )}
          {currentStep === 2 && (
            <CollegeListReviewStep
              selectedColleges={selectedColleges}
              setSelectedColleges={setSelectedColleges}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 3 && (
            <CollegePreferencesStep
              collegePreferences={collegePreferences}
              setCollegePreferences={setCollegePreferences}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}
          {currentStep === 4 && (
            <AssessmentIntroductionStep
              onStartAssessment={startAssessment}
              onPrev={prevStep}
            />
          )}
        </div>
      </div>

      {/* Data Recovery Modal */}
      <DataRecoveryModal
        isOpen={recovery.showRecoveryPrompt}
        recoveryData={recovery.recoveryData}
        onAccept={() => {
          const recoveredData = recovery.acceptRecovery();
          if (recoveredData) {
            console.log('🔄 Restoring onboarding data:', recoveredData);
            setCurrentStep(recoveredData.currentStep || 1);
            setProfileData(recoveredData.profileData || {});
            setSelectedColleges(recoveredData.selectedColleges || []);
            setCollegePreferences(recoveredData.collegePreferences || {
              intendedMajors: [],
              geographicPreferences: '',
              sizePreference: '',
              settingPreference: '',
              keyCriteria: [],
              studentUniqueInsight: ''
            });
          }
        }}
        onDecline={recovery.declineRecovery}
        title="Resume Your Application?"
        description="We found your previous onboarding session. Would you like to continue where you left off?"
      />
    </div>
  );
};

// Step 1: Profile Review
const ProfileReviewStep = ({ profileData, setProfileData, onNext }) => {
  const editField = (field) => {
    // TODO: Implement inline editing modal
    console.log(`Editing field: ${field}`);
    // For now, navigate to comprehensive profile editor
    editProfile();
  };

  const editProfile = async () => {
    // Navigate to the comprehensive academic profile editor
    // This would typically link to the StudentAcademicProfile component
    const confirmEdit = confirm(
      'This will open the full academic profile editor where you can add or modify all profile details. ' +
      'You can return to this onboarding flow afterward. Continue?'
    );
    
    if (confirmEdit) {
      // Save current progress before navigating
      await saveCurrentStep();
      navigate('/college/comprehensive-student-profile', { 
        state: { returnToOnboarding: true, onboardingStep: currentStep }
      });
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '16px',
        background: '#0d9488',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px auto'
      }}>
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: 'white',
          opacity: 0.9
        }}></div>
      </div>
      
      <h1 style={{
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '16px',
        color: '#0f172a'
      }}>
        Welcome to your college journey
      </h1>
      
      <p style={{
        fontSize: '16px',
        color: '#57534e',
        marginBottom: '32px',
        lineHeight: 1.6
      }}>
        Your parents have set up some basics to get you started. Let's review and make sure everything reflects who you really are.
      </p>

      <div style={{
        background: '#f5f5f4',
        border: '2px solid #e7e5e4',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        textAlign: 'left',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        <div style={{
          background: '#0d9488',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          display: 'inline-block',
          marginBottom: '20px'
        }}>
          From your parents
        </div>
        
        {/* Basic Information */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Basic Information</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="Name" value={profileData.name} field="name" editField={editField} required />
            <ProfileField label="Graduation Year" value={profileData.graduationYear} field="graduation-year" editField={editField} />
            <ProfileField label="State of Residency" value={profileData.state} field="state" editField={editField} />
            <ProfileField label="High School" value={profileData.highSchool} field="high-school" editField={editField} />
            <ProfileField label="High School State" value={profileData.highSchoolState} field="high-school-state" editField={editField} />
            <ProfileField label="High School Type" value={profileData.highSchoolType} field="high-school-type" editField={editField} />
          </div>
        </div>

        {/* Academic Performance */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Academic Performance</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="Weighted GPA" value={profileData.gpaWeighted} field="gpa-weighted" editField={editField} />
            <ProfileField label="Unweighted GPA" value={profileData.gpaUnweighted} field="gpa-unweighted" editField={editField} />
            <ProfileField label="GPA Scale" value={profileData.gpaScale} field="gpa-scale" editField={editField} />
            <ProfileField label="Class Rank" value={profileData.classRank} field="class-rank" editField={editField} />
            <ProfileField label="Class Size" value={profileData.classSize} field="class-size" editField={editField} />
            <ProfileField label="Curriculum Rigor" value={profileData.curriculumRigor} field="curriculum-rigor" editField={editField} />
          </div>
        </div>

        {/* Test Scores */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Test Scores</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="SAT Total" value={profileData.satTotal} field="sat-total" editField={editField} />
            <ProfileField label="SAT Math" value={profileData.satMath} field="sat-math" editField={editField} />
            <ProfileField label="SAT Reading/Writing" value={profileData.satReadingWriting} field="sat-reading-writing" editField={editField} />
            <ProfileField label="SAT Date" value={profileData.satDate} field="sat-date" editField={editField} />
            <ProfileField label="ACT Composite" value={profileData.actComposite} field="act-composite" editField={editField} />
            <ProfileField label="PSAT Total" value={profileData.psatTotal} field="psat-total" editField={editField} />
            <ProfileField label="National Merit Status" value={profileData.nationalMeritStatus} field="national-merit" editField={editField} />
          </div>
        </div>

        {/* Advanced Coursework */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Advanced Coursework</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="AP Courses Taken" value={profileData.apCoursesTaken} field="ap-taken" editField={editField} />
            <ProfileField label="AP Courses Planned" value={profileData.apCoursesPlanned} field="ap-planned" editField={editField} />
            <ProfileField label="AP Exams Passed" value={profileData.apExamsPassed} field="ap-passed" editField={editField} />
            <ProfileField label="IB Diploma Candidate" value={profileData.ibDiplomaCandidate ? 'Yes' : null} field="ib-diploma" editField={editField} />
            <ProfileField label="IB Predicted Score" value={profileData.ibPredictedScore} field="ib-score" editField={editField} />
            <ProfileField label="Dual Enrollment Credits" value={profileData.dualEnrollmentCredits} field="dual-enrollment" editField={editField} />
            <ProfileField label="Honors Courses" value={profileData.honorsCourses} field="honors" editField={editField} />
          </div>
        </div>

        {/* Academic Interests & Goals */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Academic Interests & Goals</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="Academic Interests" value={profileData.academicInterests?.join(', ')} field="academic-interests" editField={editField} />
            <ProfileField label="Intended Major" value={profileData.intendedMajor} field="intended-major" editField={editField} />
            <ProfileField label="Intended Minor" value={profileData.intendedMinor} field="intended-minor" editField={editField} />
            <ProfileField label="Career Goals" value={profileData.careerGoals} field="career-goals" editField={editField} />
          </div>
        </div>

        {/* Background & Special Circumstances */}
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginBottom: '12px' }}>Background & Special Circumstances</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            <ProfileField label="First Generation College" value={profileData.firstGenerationCollege ? 'Yes' : null} field="first-gen" editField={editField} />
            <ProfileField label="Legacy Connections" value={profileData.legacyConnections?.join(', ')} field="legacy" editField={editField} />
            <ProfileField label="Recruited Athlete Sports" value={profileData.recruitedAthleteSports?.join(', ')} field="recruited-athlete" editField={editField} />
            <ProfileField label="Underrepresented Background" value={profileData.underrepresentedBackground ? 'Yes' : null} field="underrepresented" editField={editField} />
            <ProfileField label="Special Circumstances" value={profileData.specialCircumstances} field="special-circumstances" editField={editField} isLast />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
        <button
          onClick={editProfile}
          style={{
            background: 'white',
            color: '#57534e',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            border: '2px solid #e7e5e4',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#f5f5f4';
            e.target.style.borderColor = '#d6d3d1';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'white';
            e.target.style.borderColor = '#e7e5e4';
          }}
        >
          Add/Edit Profile Details
        </button>
        <button
          onClick={onNext}
          style={{
            background: '#0d9488',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#0f766e';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = '#0d9488';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          Looks good →
        </button>
      </div>
      
      <div style={{
        background: '#f5f5f4',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '14px',
        color: '#57534e',
        textAlign: 'center'
      }}>
        You can always come back and update these details later
      </div>
    </div>
  );
};

// Step 2: College List Review
const CollegeListReviewStep = ({ selectedColleges, setSelectedColleges, onNext, onPrev }) => {
  const [showAddCollegeModal, setShowAddCollegeModal] = useState(false);
  
  const toggleCollege = (college) => {
    setSelectedColleges(prev => 
      prev.some(c => c.id === college.id)
        ? prev.filter(c => c.id !== college.id)
        : [...prev, college]
    );
  };

  const addCustomCollege = () => {
    setShowAddCollegeModal(true);
  };

  const handleCollegeAdded = (newCollege) => {
    // Add the new college to the selected colleges list
    const collegeToAdd = {
      id: newCollege.name?.toLowerCase().replace(/\s+/g, '_') || `college_${Date.now()}`,
      name: newCollege.name,
      reason: newCollege.reason || 'Added by student',
      parentAdded: false,
      category: newCollege.category || 'target'
    };
    setSelectedColleges(prev => [...prev, collegeToAdd]);
    setShowAddCollegeModal(false);
  };

  // Show all colleges from the database plus a few suggested options
  const availableColleges = [
    ...selectedColleges, // Pre-selected colleges from database
    // Add a few additional suggested colleges if not already present
    ...[
      { id: 'uchicago', name: 'UChicago', reason: 'Rigorous academics, intellectual culture' },
      { id: 'cmu', name: 'Carnegie Mellon', reason: 'Strong CS, Human-Computer Interaction' },
      { id: 'brown', name: 'Brown University', reason: 'Open curriculum, collaborative culture' },
      { id: 'northwestern', name: 'Northwestern', reason: 'Strong academics, great location' }
    ].filter(suggested => !selectedColleges.some(existing => 
      existing.name === suggested.name || existing.id === suggested.id
    ))
  ];

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '16px',
        background: '#0d9488',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px auto'
      }}>
        <div style={{
          width: '28px',
          height: '20px',
          background: 'white',
          borderRadius: '4px',
          opacity: 0.9,
          position: 'relative'
        }}>
          <div style={{
            width: '20px',
            height: '2px',
            background: '#0d9488',
            position: 'absolute',
            top: '6px',
            left: '4px'
          }}></div>
          <div style={{
            width: '16px',
            height: '2px',
            background: '#0d9488',
            position: 'absolute',
            top: '10px',
            left: '4px'
          }}></div>
          <div style={{
            width: '12px',
            height: '2px',
            background: '#0d9488',
            position: 'absolute',
            top: '14px',
            left: '4px'
          }}></div>
        </div>
      </div>
      
      <h1 style={{
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '16px',
        color: '#0f172a'
      }}>
        Review your college list
      </h1>
      
      <p style={{
        fontSize: '16px',
        color: '#57534e',
        marginBottom: '32px',
        lineHeight: 1.6
      }}>
        Based on your interests and preferences, here are some colleges to consider. Select any that interest you, remove ones that don't fit, or add your own.
      </p>

      <div style={{
        background: '#f5f5f4',
        border: '2px solid #e7e5e4',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        textAlign: 'left'
      }}>
        <div style={{
          background: '#e91e63',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          display: 'inline-block',
          marginBottom: '16px'
        }}>
          Parent suggestions + smart recommendations
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {availableColleges.map((college) => {
            const isSelected = selectedColleges.some(c => c.id === college.id);
            return (
              <button
                key={college.id}
                onClick={() => toggleCollege(college)}
                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? 'border-teal-500 bg-teal-50 transform -translate-y-0.5'
                    : 'border-stone-200 hover:border-teal-500 hover:-translate-y-0.5'
                }`}
              >
                {college.parentAdded && (
                  <span className="absolute top-2 right-2 text-sm">👥</span>
                )}
                <div className="text-sm font-semibold text-slate-900 mb-1">{college.name}</div>
                <div className="text-sm text-stone-500 leading-tight">{college.reason}</div>
              </button>
            );
          })}
        </div>
        
        <div className="text-center mt-4">
          <button 
            onClick={addCustomCollege}
            className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2 rounded-lg text-sm font-medium border border-stone-200 transition-colors"
          >
            + Add your own college
          </button>
        </div>
      </div>

      <div className="flex gap-3 justify-center mb-4">
        <button
          onClick={onPrev}
          className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-6 py-3 rounded-lg text-lg font-medium border border-stone-200 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all transform hover:-translate-y-0.5"
        >
          Continue with selected →
        </button>
      </div>
      
      <div className="bg-stone-100 rounded-lg p-3 text-sm text-stone-600 text-center">
        Don't worry - you can always add or remove colleges later as you learn more
      </div>

      {/* Add College Modal */}
      {showAddCollegeModal && (
        <AddCollegeModal 
          onClose={() => setShowAddCollegeModal(false)}
          onCollegeAdded={handleCollegeAdded}
        />
      )}
    </div>
  );
};

// Step 3: College Preferences & Student Context
const CollegePreferencesStep = ({ collegePreferences, setCollegePreferences, onNext, onPrev }) => {
  const [editingField, setEditingField] = useState(null);
  
  const handlePreferenceUpdate = (field, value) => {
    setCollegePreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const majorOptions = [
    'Computer Science', 'Engineering', 'Business', 'Pre-Med/Biology', 
    'Psychology', 'Economics', 'Political Science', 'English', 
    'Mathematics', 'Physics', 'Chemistry', 'History', 'Philosophy',
    'Art/Design', 'Music', 'Architecture', 'Nursing', 'Education',
    'Communications', 'Environmental Science', 'International Relations',
    'Undecided/Exploring'
  ];

  const criteriaOptions = [
    'Strong academics in my major', 'Research opportunities', 'Small class sizes',
    'Diverse student body', 'Study abroad programs', 'Internship opportunities',
    'Campus culture/vibe', 'Athletic programs', 'Greek life', 'Location/weather',
    'Financial aid', 'Career services', 'Housing quality', 'Food options',
    'Mental health support', 'Entrepreneurship programs', 'Honor programs'
  ];

  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-2xl mx-auto mb-6">
        🎯
      </div>
      
      <h1 className="text-3xl font-bold mb-4 text-slate-900">
        Tell us about your college preferences
      </h1>
      
      <p className="text-lg text-stone-600 mb-8 leading-relaxed">
        Help us understand what you're looking for in a college. This will personalize your recommendations and insights.
      </p>

      <div className="text-left space-y-6">
        {/* Intended Majors */}
        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            What are you thinking about studying? (Select all that interest you)
          </label>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-stone-200 rounded-lg p-3">
            {majorOptions.map(major => (
              <label key={major} className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={collegePreferences.intendedMajors.includes(major)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handlePreferenceUpdate('intendedMajors', [...collegePreferences.intendedMajors, major]);
                    } else {
                      handlePreferenceUpdate('intendedMajors', collegePreferences.intendedMajors.filter(m => m !== major));
                    }
                  }}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-stone-700">{major}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Geographic Preferences */}
        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            Where are you open to going to college?
          </label>
          <select
            value={collegePreferences.geographicPreferences}
            onChange={(e) => handlePreferenceUpdate('geographicPreferences', e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg"
          >
            <option value="">Select preference...</option>
            <option value="anywhere">Anywhere in the US</option>
            <option value="home-state">Prefer my home state</option>
            <option value="nearby-states">My region/nearby states</option>
            <option value="far-from-home">Want to go far from home</option>
            <option value="specific-regions">Specific regions (Northeast, West Coast, etc.)</option>
            <option value="warm-weather">Warm weather states only</option>
            <option value="major-cities">Near major cities</option>
          </select>
        </div>

        {/* Size Preference */}
        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            What size school appeals to you?
          </label>
          <select
            value={collegePreferences.sizePreference}
            onChange={(e) => handlePreferenceUpdate('sizePreference', e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg"
          >
            <option value="">Select preference...</option>
            <option value="small">Small (under 3,000 students)</option>
            <option value="medium">Medium (3,000-10,000 students)</option>
            <option value="large">Large (10,000-25,000 students)</option>
            <option value="very-large">Very Large (over 25,000 students)</option>
            <option value="no-preference">No strong preference</option>
          </select>
        </div>

        {/* Setting Preference */}
        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            What type of setting do you prefer?
          </label>
          <select
            value={collegePreferences.settingPreference}
            onChange={(e) => handlePreferenceUpdate('settingPreference', e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg"
          >
            <option value="">Select preference...</option>
            <option value="urban">Urban - In or near a city</option>
            <option value="suburban">Suburban - Near a city with campus feel</option>
            <option value="college-town">College Town - School is the main focus</option>
            <option value="rural">Rural - Countryside/nature focused</option>
            <option value="no-preference">Open to any setting</option>
          </select>
        </div>

        {/* Key Criteria */}
        <div>
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            What's most important to you in a college? (Select your top 5)
          </label>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-stone-200 rounded-lg p-3">
            {criteriaOptions.map(criteria => (
              <label key={criteria} className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={collegePreferences.keyCriteria.includes(criteria)}
                  onChange={(e) => {
                    if (e.target.checked && collegePreferences.keyCriteria.length < 5) {
                      handlePreferenceUpdate('keyCriteria', [...collegePreferences.keyCriteria, criteria]);
                    } else if (!e.target.checked) {
                      handlePreferenceUpdate('keyCriteria', collegePreferences.keyCriteria.filter(c => c !== criteria));
                    }
                  }}
                  disabled={!collegePreferences.keyCriteria.includes(criteria) && collegePreferences.keyCriteria.length >= 5}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                <span className={`text-sm ${!collegePreferences.keyCriteria.includes(criteria) && collegePreferences.keyCriteria.length >= 5 ? 'text-stone-400' : 'text-stone-700'}`}>
                  {criteria}
                </span>
              </label>
            ))}
          </div>
          {collegePreferences.keyCriteria.length >= 5 && (
            <p className="text-xs text-amber-600 mt-1">Maximum 5 criteria selected</p>
          )}
        </div>

        {/* Student Unique Insight */}
        <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            One more thing before we start...
          </label>
          <p className="text-sm text-stone-600 mb-3">
            What's one thing about yourself that you wish colleges could understand that's hard to show in an application?
          </p>
          <textarea
            value={collegePreferences.studentUniqueInsight}
            onChange={(e) => handlePreferenceUpdate('studentUniqueInsight', e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-lg"
            rows="3"
            placeholder="E.g., I'm actually really creative even though my grades don't show it, I care more about learning than grades, I work best when I'm passionate about something..."
          />
        </div>
      </div>

      <div className="flex gap-3 justify-center mt-8">
        <button
          onClick={onPrev}
          className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-6 py-3 rounded-lg text-lg font-medium border border-stone-200 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all transform hover:-translate-y-0.5"
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

// Step 4: Assessment Introduction
const AssessmentIntroductionStep = ({ onStartAssessment, onPrev }) => {
  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-2xl mx-auto mb-6">
        🧭
      </div>
      
      <h1 className="text-3xl font-bold mb-4 text-slate-900">
        One more step to get you set up
      </h1>
      
      <p className="text-lg text-stone-600 mb-8 leading-relaxed">
        Your profile and college list look good. Now let's look for patterns in how you think and what interests you - this will help personalize everything you see going forward.
      </p>

      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-6">
        <div className="text-lg font-semibold mb-3 text-slate-900">This assessment will help identify:</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">🔍</span>
            <span>Patterns in how you approach problems and challenges</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">🎯</span>
            <span>What types of environments and activities you're drawn to</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">✍️</span>
            <span>Experiences and stories worth exploring for essays</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">🏫</span>
            <span>Criteria to help you evaluate colleges that might be good fits</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">💡</span>
            <span>What truly motivates you and environments where you thrive</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <span className="text-yellow-600 text-lg">✨</span>
            <span>Unique perspective and contributions you bring to communities</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-2 bg-stone-100 rounded-full px-4 py-2 mb-8 w-fit mx-auto">
        <span className="text-lg">⏱️</span>
        <span className="text-sm text-stone-600">15 minutes • Conversational format • Save progress anytime</span>
      </div>

      <div className="flex gap-3 justify-center mb-4">
        <button
          onClick={onPrev}
          className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-6 py-3 rounded-lg text-lg font-medium border border-stone-200 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onStartAssessment}
          className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-all transform hover:-translate-y-0.5"
        >
          Start conversation →
        </button>
      </div>
      
      <div className="bg-stone-100 rounded-lg p-3 text-sm text-stone-600 text-center">
        Your progress is automatically saved. You can pause and return anytime.
      </div>
    </div>
  );
};

// Helper component for profile fields
const ProfileField = ({ label, value, field, editField, required = false, isLast = false }) => {
  const hasValue = value !== null && value !== undefined && value !== '';
  
  return (
    <div className={`flex items-center justify-between py-2 ${!isLast ? 'border-b border-stone-200' : ''}`}>
      <span className="text-sm font-medium text-stone-600">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-medium ${hasValue ? 'text-slate-900' : 'text-stone-400'}`}>
          {hasValue ? value : 'Not provided'}
        </span>
        <button 
          onClick={() => editField(field)}
          className="text-sm text-teal-600 hover:bg-teal-50 px-2 py-1 rounded transition-colors"
        >
          {hasValue ? 'Edit' : 'Add'}
        </button>
      </div>
    </div>
  );
};

// Add College Modal Component
const AddCollegeModal = ({ onClose, onCollegeAdded }) => {
  const [collegeName, setCollegeName] = useState('');
  const [collegeLocation, setCollegeLocation] = useState('');
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('target');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollegeId, setSelectedCollegeId] = useState(null);

  // Debounced search for global_colleges table
  useEffect(() => {
    const searchColleges = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('global_colleges')
          .select('id, name, city, state, usnwr_ranking, acceptance_rate')
          .or(`name.ilike.%${searchTerm}%,aliases.cs.{${searchTerm}}`)
          .order('usnwr_ranking', { ascending: true, nullsFirst: false })
          .limit(10);

        if (error) throw error;

        setSearchResults(data || []);
      } catch (error) {
        console.error('Error searching colleges:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchColleges, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const selectCollege = (college) => {
    setCollegeName(college.name);
    // Handle potential null values for city and state
    const location = [college.city, college.state].filter(Boolean).join(', ');
    setCollegeLocation(location);
    setSelectedCollegeId(college.id);
    setSearchResults([]);
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!collegeName.trim()) return;

    const newCollege = {
      name: collegeName,
      location: collegeLocation,
      reason: reason || 'Interest in academic programs',
      category,
      global_college_id: selectedCollegeId // Include the global_college_id if available
    };

    onCollegeAdded(newCollege);
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Add College</h3>
          <button 
            onClick={onClose}
            className="text-stone-500 hover:text-stone-700 text-xl"
          >
            ×
          </button>
        </div>

        {!showForm ? (
          // Search Phase
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Search for a college
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type college name..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  onChange={(e) => handleSearch(e.target.value)}
                  value={searchTerm}
                />
                {isSearching && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              {searchTerm.length > 0 && searchTerm.length < 2 && (
                <p className="mt-1 text-xs text-stone-500">Type at least 2 characters to search</p>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-stone-700 mb-2">Select from results:</div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.map((college) => (
                    <button
                      key={college.id}
                      onClick={() => selectCollege(college)}
                      className="w-full text-left p-3 border border-stone-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{college.name}</div>
                          <div className="text-sm text-stone-600 mt-0.5">
                            {[college.city, college.state].filter(Boolean).join(', ')}
                            {college.usnwr_ranking && (
                              <span className="ml-2 text-xs text-stone-500">
                                • Rank #{college.usnwr_ranking}
                              </span>
                            )}
                            {college.acceptance_rate && (
                              <span className="ml-2 text-xs text-stone-500">
                                • {(college.acceptance_rate * 100).toFixed(1)}% acceptance
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <button
                onClick={() => {
                  setShowForm(true);
                  setSelectedCollegeId(null); // Clear selected ID for manual entry
                  setCollegeName('');
                  setCollegeLocation('');
                }}
                className="w-full text-left p-3 border border-stone-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors"
              >
                <div className="font-medium text-slate-900">+ Add college not in list</div>
                <div className="text-sm text-stone-500">Manually enter college details</div>
              </button>
            </div>
          </div>
        ) : (
          // Form Phase
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                College Name *
              </label>
              <input
                type="text"
                value={collegeName}
                onChange={(e) => setCollegeName(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="e.g., University of California, Berkeley"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Location (Optional)
              </label>
              <input
                type="text"
                value={collegeLocation}
                onChange={(e) => setCollegeLocation(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="e.g., Berkeley, CA"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Why are you interested? (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                rows="2"
                placeholder="e.g., Strong engineering program, great location..."
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="reach">reach/Reach</option>
                <option value="target">Target/Match</option>
                <option value="safety">Safety</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className="flex-1 px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
              >
                Back to Search
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Add College
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default StudentOnboardingFlow;